<template>
    <v-container fill-height class="align-start">
        <v-row
            no-gutters
            class="align-self-center"
            align="center"
            justify="center"
        >
            <v-col>
                <v-row
                    align="center"
                    justify="center"
                    no-gutters
                >
                    <v-col class="form">
                        <v-card
                            class="elevation-12"
                            width="89vw"
                            max-width="500px"
                            :loading="loading && 'white'"
                        >
                            <v-toolbar
                                color="primary"
                                dark
                                flat
                            >
                                <v-toolbar-title>
                                    <span v-if="copyOfRecipient.recipient_info.name">Editing <span class="font-weight-bold">{{ copyOfRecipient.recipient_info.name }}</span></span>
                                    <span v-else>New Recipient</span>
                                </v-toolbar-title>
                            </v-toolbar>
                            <v-card-text>
                                <v-form ref="form">
                                    <!-- Country -->
                                    <v-autocomplete
                                        ref="country"
                                        v-model="copyOfRecipient.country"
                                        v-validate="paymentProviderRules.address"
                                        autocomplete="section-address country-name"
                                        :error-messages="errors.first('country')"
                                        name="country"
                                        label="Country"
                                        :items="countries"
                                        item-text="country"
                                        item-value="abbreviation"
                                        item-color="primary"
                                        outlined
                                        color="primary"
                                        @change="filterDeliveryMethods()"
                                    />

                                    <!-- Delivery Method -->
                                    <v-expand-transition>
                                        <div v-if="deliveryMethods[0]">
                                            <v-autocomplete
                                                key="delivery methods"
                                                v-model="copyOfRecipient.recipient_info.payment_type"
                                                v-validate="'required'"
                                                :error-messages="errors.first('delivery method')"
                                                aria-required
                                                required
                                                name="Delivery Method"
                                                label="Delivery Method"
                                                :items="deliveryMethods"
                                                return-object
                                                item-text="method"
                                                item-color="primary"
                                                color="primary"
                                                outlined
                                                @change="filterDeliveryMethods()"
                                            />
                                        </div>
                                    </v-expand-transition>

                                    <!-- Bank -->
                                    <v-expand-transition v-if="isFlutterwaveUsed">
                                        <v-autocomplete
                                            v-model="selectedBank"
                                            v-validate="'required'"
                                            :error-messages="errors.first('bank')"
                                            aria-required
                                            required
                                            name="bank"
                                            :label="getPaymentTypeName(selectedPaymentType.method)"
                                            :items="filteredBanks"
                                            item-text="Name"
                                            return-object
                                            item-color="primary"
                                            color="primary"
                                            outlined
                                        />
                                    </v-expand-transition>

                                    <!-- Payment Provider -->
                                    <v-expand-transition v-else>
                                        <div v-if="filteredPaymentProviders && selectedPaymentType.method !== 'pickup'">
                                            <v-autocomplete
                                                key="payment provider"
                                                v-model="selectedPaymentProvider"
                                                v-validate="'required'"
                                                :error-messages="errors.first('payment provider')"
                                                aria-required
                                                required
                                                name="payment provider"
                                                :label="getPaymentTypeName(selectedPaymentType.method)"
                                                :items="filteredPaymentProviders"
                                                item-text="name"
                                                return-object
                                                item-color="primary"
                                                color="primary"
                                                outlined
                                            />
                                        </div>
                                    </v-expand-transition>

                                    <!-- Bank Branch -->
                                    <v-expand-transition v-if="isFlutterwaveUsed && isBankBranchesAvailable">
                                        <v-autocomplete
                                            v-model="selectedBankBranch"
                                            v-validate="'required'"
                                            :error-messages="errors.first('bank branch')"
                                            aria-required
                                            required
                                            name="bank branch"
                                            label="Bank Branch"
                                            :items="bankBranches"
                                            item-text="BranchName"
                                            return-object
                                            item-color="primary"
                                            color="primary"
                                            outlined
                                        />
                                    </v-expand-transition>

                                    <!-- Branch Name -->
                                    <v-expand-transition>
                                        <div
                                            v-if="selectedPaymentProvider"
                                            key="branches"
                                        >
                                            <!-- If there are selectable branches... -->
                                            <v-expand-transition>
                                                <div v-if="(paymentProviderBranches.length > 1) && !loading">
                                                    <v-autocomplete
                                                        v-model="selectedBranch"
                                                        name="branch"
                                                        label="Branch"
                                                        :items="selectedPaymentProvider.branches"
                                                        item-color="primary"
                                                        color="primary"
                                                        outlined
                                                    />
                                                </div>
                                            </v-expand-transition>

                                            <!--
                                                There is apparently a scenario where a payment provider in a country will require
                                                a branch name even if we do not currently have it in our db. We'll need to provide
                                                a way to notify the form for this (perhaps within Rules).

                                                On top of this, Sometimes a Branch code is required. This requirement is not currently
                                                available to me in the db.
                                            -->
                                        </div>
                                    </v-expand-transition>

                                    <!-- Email -->
                                    <v-text-field
                                        key="email"
                                        v-model="copyOfRecipient.recipient_info.email"
                                        v-validate="paymentProviderRules.email_address"
                                        validate-on-blur
                                        name="email"
                                        label="Email"
                                        color="primary"
                                        outlined
                                        :error-messages="errors.first('email')"
                                    />

                                    <!-- Phone -->
                                    <v-text-field
                                        v-model="copyOfRecipient.recipient_info.phone"
                                        v-validate="phoneRules"
                                        validate-on-blur
                                        name="phone"
                                        :aria-required="paymentTypeIsMobile"
                                        :required="paymentTypeIsMobile"
                                        label="Phone number"
                                        color="primary"
                                        outlined
                                        :error-messages="errors.first('phone')"
                                    />

                                    <!-- Confirm Phone -->
                                    <v-expand-transition>
                                        <div v-if="paymentTypeIsMobile">
                                            <v-text-field
                                                v-model="confirmPhone"
                                                v-validate="phoneRules"
                                                validate-on-blur
                                                name="confirm phone"
                                                aria-required
                                                required
                                                label="Confirm Phone number"
                                                color="primary"
                                                outlined
                                                :error-messages="errors.first('confirm phone')"
                                            />
                                        </div>
                                    </v-expand-transition>

                                    <!-- Account Number -->
                                    <v-expand-transition>
                                        <div v-if="paymentTypeIsBank">
                                            <v-text-field
                                                v-model="copyOfRecipient.recipient_info.account_number"
                                                v-validate="accountNumberRules"
                                                name="account number"
                                                ref="account_number"
                                                aria-required
                                                required
                                                label="Recipient Account number"
                                                color="primary"
                                                outlined
                                                validate-on-blur
                                                :error-messages="errors.first('account number')"
                                            />
                                        </div>
                                    </v-expand-transition>

                                    <!-- Confirm Account Number -->
                                    <v-expand-transition v-if="!isAllowToValidateAccount">
                                        <div v-if="paymentTypeIsBank">
                                            <v-text-field
                                                ref="account-confirmation"
                                                v-model="confirmAccountNumber"
                                                label="Confirm Recipient Account Number"
                                                name="account confirmation"
                                                color="primary"
                                                outlined
                                            />
                                        </div>
                                    </v-expand-transition>

                                    <v-expand-transition>
                                        <!-- Name -->
                                        <v-text-field
                                            v-if="showRecipientNameField"
                                            key="name"
                                            v-model="copyOfRecipient.recipient_info.name"
                                            v-validate="paymentProviderRules.name"
                                            :error-messages="errors.first('full name')"
                                            validate-on-blur
                                            name="full name"
                                            aria-required
                                            required
                                            label="Full Name"
                                            color="primary"
                                            outlined
                                        />
                                    </v-expand-transition>

                                    <v-expand-transition>
                                        <v-row v-if="showRecipientNameRow">
                                            <v-col>
                                                <p class="title green--text font-weight-bold mb-7 text-center">
                                                    {{ copyOfRecipient.recipient_info.name }}
                                                </p>
                                            </v-col>
                                        </v-row>
                                    </v-expand-transition>

                                    <!-- Address -->
                                    <v-expansion-panels
                                        v-model="expansionPanel"
                                        class="v-expansion-panels--flat"
                                        flat
                                        tile
                                        accordion
                                        hover
                                    >
                                        <v-expansion-panel
                                            flat
                                        >
                                            <v-expansion-panel-header
                                                expand-icon="mdi-menu-down"
                                            >
                                                Address
                                            </v-expansion-panel-header>
                                            <v-expansion-panel-content>
                                                <v-form @keyup.native.enter="save().address()">
                                                    <div>
                                                        <!-- Street Address -->
                                                        <v-text-field
                                                            v-model="copyOfRecipient.recipient_info.address"
                                                            v-validate="paymentProviderRules.address"
                                                            autocomplete="section-address address-level1"
                                                            type="text"
                                                            label="Street Address"
                                                            name="street address"
                                                            color="primary"
                                                            outlined
                                                            dense
                                                            :error-messages="errors.first('street address')"
                                                            @focus="$event.target.select()"
                                                        />
                                                        <!-- Apartment Number -->
                                                        <v-text-field
                                                            v-model="copyOfRecipient.recipient_info.building_number"
                                                            autocomplete="section-address address-apartment-number"
                                                            type="text"
                                                            label="Apartment Number"
                                                            name="apartment number"
                                                            color="primary"
                                                            outlined
                                                            dense
                                                            @focus="$event.target.select()"
                                                        />
                                                        <!-- City -->
                                                        <v-text-field
                                                            v-model="copyOfRecipient.recipient_info.city"
                                                            v-validate="paymentProviderRules.address"
                                                            autocomplete="section-address shipping address-level2"
                                                            type="text"
                                                            label="City"
                                                            name="city"
                                                            color="primary"
                                                            outlined
                                                            dense
                                                            :error-messages="errors.first('city')"
                                                            @focus="$event.target.select()"
                                                        />

                                                        <!-- State -->
                                                        <v-text-field
                                                            v-model="copyOfRecipient.recipient_info.state"
                                                            v-validate="paymentProviderRules.address"
                                                            autocomplete="section-address region"
                                                            type="text"
                                                            label="State"
                                                            name="state"
                                                            color="primary"
                                                            outlined
                                                            dense
                                                            :error-messages="errors.first('state')"
                                                            @focus="$event.target.select()"
                                                        />

                                                        <!-- Zipcode -->
                                                        <v-text-field
                                                            v-model="copyOfRecipient.recipient_info.zipcode"
                                                            v-validate="paymentProviderRules.address"
                                                            autocomplete="section-address postal-code"
                                                            type="text"
                                                            label="Zipcode"
                                                            name="zip code"
                                                            color="primary"
                                                            outlined
                                                            dense
                                                            :error-messages="errors.first('zip code')"
                                                            @focus="$event.target.select()"
                                                        />
                                                    </div>
                                                </v-form>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                    </v-expansion-panels>
                                </v-form>
                            </v-card-text>
                            <v-card-actions class="pa-6">
                                <v-spacer />
                                <v-btn
                                    :to="{ name: 'recipient-list' }"
                                    color="red darken-2"
                                    class="px-6"
                                    text
                                    large
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    class="px-6"
                                    color="primary"
                                    dark
                                    depressed
                                    large
                                    :recipientInfo="recipient"
                                    @click.native="validateThenSave"
                                >
                                    Save
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import FlutterwaveService from '../services/FlutterwaveService';
    import ServerUtils from '../services/ServerUtils';

    export default {
        props: {
            recipient: {
                type: Object,
                default() {
                    return {
                        user_id: this.$store.state.user.id,
                        recipient_info: {
                            email: null,
                            phone: null,
                            account: null,
                            name: null,
                            branch: null,
                            branch_code: null,
                            address: null,
                            building_number: null,
                            city: null,
                            state: null,
                            country: null,
                            zipcode: null,
                            payment_type: null,
                            payment_provider: null,
                        },
                    };
                },
            },
            returnToPreviousPage: {
                type: Function,
                default() {
                    this.$router.push({ name: 'recipient-list' });
                },
            },
        },
        data,
        computed: getComputed(),
        mounted,
        beforeDestroy,
        methods: getMethods(),
        watch: {
            'filteredPaymentProviders': {
                deep: true,
                handler: function (providers) {
                    if (!this.copyOfRecipient.id) {
                        Vue.set(this, 'selectedPaymentProvider', providers[0])
                    }
                },
            },
            // eslint-disable-next-line object-shorthand
            isAbleToValidateAccount: function (newVal) {
                if (newVal) {
                    this.verifyAccount();
                }
            },
            'selectedBank.Id': function (newVal) {
                if (this.isBankBranchesAvailable) {
                    this.loadBankBranches(newVal);
                }
                if (this.isAbleToValidateAccount) {
                    this.verifyAccount();
                }
            },
            'copyOfRecipient.recipient_info.account_number': function () {
                if (this.isAbleToValidateAccount) {
                    this.verifyAccount();
                }
            },
        },
    };

    function data() {
        return {
            loading: false,
            addNew: false,
            selectedPaymentProvider: null,
            selectedBranch: {},
            selectedBranchCode: null,
            matcherPaymentPurpose: '',
            otherPaymentPurpose: '',
            copyOfRecipient: Vue.util.extend({}, this.recipient),
            recipientId: null,
            banks: [],
            selectedBank: {},
            bankBranches: [],
            selectedBankBranch: {},
            pageTitle: 'Recipient Details',
            expansionPanel: null,
            countries: [],
            confirmPhone: '',
            confirmAccountNumber: '',
            paymentPurposes: [
                'None Selected',
                'Savings',
                'Non-recurring expenses',
                'Family',
                'Insurance',
                'Estate',
                'Education',
                'Medical',
                'Travel',
                'Trade',
                'Utility',
                'Taxes',
                'Loans',
                'Other',
            ],
            deliveryMethods: [],
            paymentProviders: [],
            branches: [],
            countriesToValidateAccount: [
                'NG',
                // 'GH',
            ],
            countriesToAllowBankBranches: [
                'TZ',
                'UG',
            ],
        };
    }

    function mounted() {
        const vm = this;
        loadCountries();
        setupRecipientData();

        function setupRecipientData() {
            getDeliveryMethodProviders();
            getRecipientData();
            getProviderBranches();
            vm.filterDeliveryMethods();

            function getDeliveryMethodProviders() {
                Vue.backendApi().deliveryMethods().deliveryMethodProviders().then(success, error);

                function success(response) {
                    Vue.set(vm, 'paymentProviders', response.data);
                }

                function error(err) {
                    vm.$swal({
                        type: 'error',
                        title: 'Error retrieving payment providers',
                        text: err,
                    });
                }
            }

            function getRecipientData() {
                Vue.set(vm, 'selectedPaymentProvider', vm.copyOfRecipient.recipient_info.payment_provider);
                Vue.set(vm, 'selectedBank', vm.copyOfRecipient.recipient_info.bank);
                Vue.set(vm, 'selectedBranch', vm.copyOfRecipient.recipient_info.branch);
                Vue.set(vm, 'confirmPhone', vm.copyOfRecipient.recipient_info.phone);
            }

            function getProviderBranches() {
                Vue.backendApi().deliveryMethods().providerBranches().then(success, error);
                function success(response) {
                    Vue.set(vm, 'branches', response.data);
                }
                function error(err) {
                    vm.$swal({
                        type: 'error',
                        title: 'Error retrieving branches for the selected payment provider',
                        text: err,
                    });
                }
            }
        }

        function loadCountries() {
            Vue.backendApi()
                .countries()
                .list()
                .then(success, error);

            function success(response) {
                Vue.set(vm, 'countries', response.data);
            }

            function error(err) {
                vm.$swal({
                    type: 'error',
                    title: 'Error loading countries',
                    text: err,
                });
            }
        }
    }

    function getMethods() {
        return {
            validateThenSave,
            saveRecipient,
            filterDeliveryMethods,
            countryAbbrev,
            loadBanks,
            loadBankBranches,
            verifyAccount,
            getPaymentTypeName,
        };

        function getPaymentTypeName (paymentTypeMethod) {
            switch (paymentTypeMethod) {
                case 'mobile-money':
                    return 'Mobile Money'
                case 'kaoshi-test':
                    return 'Kaoshi Test'
                default:
                    return 'Bank';
            }
        }

        function validateThenSave() {
            const vm = this;
            vm.$validator.validateAll().then(submitIfValid);
            function submitIfValid() {
                if (!vm.copyOfRecipient.recipient_info.name) {
                    vm.$swal({
                        type: 'error',
                        title: 'Recipient name cannot be empty',
                        text: 'Please add a recipient or select a saved recipient',
                    });
                    return;
                }

                const formIsValid = vm.$refs.form.errors.items.length === 0;
                if (formIsValid) {
                    vm.saveRecipient(vm.copyOfrecipient);
                }
            }
        }

        function saveRecipient() {
            const vm = this;
            vm.loading = true;
            const payload = JSON.parse(JSON.stringify(this.copyOfRecipient));
            delete payload.hash;
            delete payload.created_at;
            delete payload.updated_at;
            const recipientId = this.copyOfRecipient.id ? this.copyOfRecipient.id : null;
            delete payload.id;
            if(payload.recipient_info.payment_type) {
                delete payload.recipient_info.payment_type.providers;
            }

            // Store info if Flutterwave was used
            payload.recipient_info.is_flutterwave = vm.isFlutterwaveUsed;

            /**
             * If Flutterwave IS NOT used and payment provider selected -
             * save payment provider
             */
            if (!vm.isFlutterwaveUsed && vm.selectedPaymentProvider) {
                payload.recipient_info.payment_provider = vm.selectedPaymentProvider;
                payload.institution_name = vm.selectedPaymentProvider.name;
            }

            /**
             * If Flutterwave IS used and bank selected -
             * save bank
             */
            if (vm.isFlutterwaveUsed && vm.selectedBank) {
                payload.recipient_info.bank = vm.selectedBank;
                payload.recipient_info.bank_code = vm.selectedBank.Code;
                payload.institution_name = vm.selectedBank.Name;
            }

            /**
             * If Flutterwave IS used and branch selected -
             * save branch
             */
            if (vm.isFlutterwaveUsed && vm.selectedBankBranch) {
                payload.recipient_info.branch = vm.selectedBankBranch;
                payload.recipient_info.branch_code = vm.selectedBankBranch.BranchCode;
            }

            /**
             * If Flutterwave IS NOT used and branch selected -
             * save payment branch
             */
            if (!vm.isFlutterwaveUsed && vm.selectedBranch) {
                payload.recipient_info.branch = vm.selectedBranch;
            }

            /**
             * If Flutterwave IS NOT used and branch code selected -
             * save payment branch code
             */
            if (!vm.isFlutterwaveUsed && vm.selectedBranchCode) {
                payload.recipient_info.branch_code = vm.selectedBranchCode;
            }

            // Prefill recipient type by payment type
            payload.recipient_type = vm.copyOfRecipient.recipient_info.payment_type.method;

            if (ServerUtils.isDevOrLocal() && payload.recipient_type === 'kaoshi-test') {
                payload.institution_name = 'COMPLETED';
            }

            Vue.backendApi().user(this.$store.state.user.id)
                .recipient(recipientId).storeRecipient(payload)
                .then(success, error);

            function success() {
                vm.loading = false;
                vm.$swal({
                    type: 'success',
                    title: 'Recipient saved!',
                    text: 'Click okay to continue',
                });
                vm.$router.go(-1);
            }

            function error(err) {
                vm.loading = false;
                vm.$swal({
                    type: 'error',
                    title: 'Error saving recipient',
                    text: err,
                });
            }
        }

        function filterDeliveryMethods() {
            const vm = this;
            Vue.backendApi().deliveryMethods().deliveryMethods().then(success, error);
            function success(response) {
                const isDevOrLocal = ServerUtils.isDevOrLocal();
                const methods = response.data.filter(
                    (deliveryMethod) => (isDevOrLocal && deliveryMethod.method === 'kaoshi-test') || deliveryMethod.providers.some(
                        (provider) => provider.country_abbreviation === vm.copyOfRecipient.country,
                    ),
                );

                Vue.set(vm, 'deliveryMethods', methods);

                if (vm.isFlutterwaveAvailable) {
                    vm.loadBanks(vm.countryAbbrev());
                }
            }
            function error(err) {
                vm.$swal({
                    type: 'error',
                    title: 'Error retrieving delivery methods',
                    text: err,
                });
                return null;
            }
        }

        function countryAbbrev() {
            const posterCurrency = this.copyOfRecipient.country;
            return posterCurrency;
        }

        async function loadBanks(countryAbbrev) {
            this.loading = true;
            if (!countryAbbrev) {
                this.$swal({
                    type: 'error',
                    title: 'Poster country is empty',
                });
            }
            try {
                const banks = await FlutterwaveService.getBanksByCountry(countryAbbrev);
                if (banks.length === 0) {
                    this.$swal({
                        type: 'error',
                        title: 'No banks found',
                    });
                } else {
                    this.banks = banks;
                }
            } catch (err) {
                this.$swal({
                    type: 'error',
                    title: 'Error loading banks',
                    text: err,
                });
            } finally {
                this.loading = false;
            }
        }

        async function loadBankBranches(bankId) {
            this.loading = true;
            try {
                const bankBranches = await FlutterwaveService.getBranchesByBankId(bankId);
                this.bankBranches = bankBranches;
            } catch (error) {
                this.$swal({
                    type: 'error',
                    title: 'Error loading bank branches',
                    text: error,
                });
            } finally {
                this.loading = false;
            }
        }

        async function verifyAccount() {
            if(!this.isAllowToValidateAccount) return

            const vm = this;
            setTimeout(async () => {
                if (vm.$refs.account_number.errorMessages.length === 0 && vm.copyOfRecipient.recipient_info.account_number && vm.selectedBank.Code) {
                    try {
                        vm.loading = true;
                        let accountName = '';

                        if(vm.copyOfRecipient.country === 'GH') {
                            const payload = {
                                country: vm.copyOfRecipient.country,
                                account_number: vm.copyOfRecipient.recipient_info.account_number,
                                bank_code: vm.selectedBank.Code,
                            };

                            const response = await Vue.backendApi().paystack().resolveAccount(payload);
                            accountName = response.data.account_name;
                        } else {
                            const { data } = await FlutterwaveService.verifyAccount(vm.copyOfRecipient.recipient_info.account_number, vm.selectedBank.Code);
                            accountName = data.data.accountname;
                        }

                        if (!accountName) {
                            this.$set(vm.copyOfRecipient.recipient_info, 'name', '');
                            vm.$swal({
                                type: 'error',
                                title: 'Failed to Verify Bank Account',
                                text: 'Wrong Account Number or Bank Name',
                            });
                        } else {
                            const account_name = accountName.replace(/, /g, '');
                            this.$set(vm.copyOfRecipient.recipient_info, 'name', account_name);
                        }
                    } catch (error) {
                        this.$set(vm.copyOfRecipient.recipient_info, 'name', '');
                        vm.$swal({
                            type: 'error',
                            title: 'Failed to verify account',
                            text: error,
                        });
                    } finally {
                        vm.loading = false;
                    }
                }
            });
        }
    }

    function getComputed() {
        return {
            selectedPaymentType,
            filteredPaymentProviders,
            paymentProviderBranches,
            pageHeaderClass,
            paymentTypeIsBank,
            paymentTypeIsMobile,
            paymentTypeIsPickup,
            phoneRules,
            accountNumberRules,
            paymentProviderRules,
            isFlutterwaveAvailable,
            isFlutterwaveUsed,
            isAllowToValidateAccount,
            isAbleToValidateAccount,
            isBankBranchesAvailable,
            filteredBanks,
            show: {
                get() {
                    return this.value;
                },
                set(value) {
                    this.$emit('input', value);
                },
            },
            showRecipientNameRow,
            showRecipientNameField,
        };

        function selectedPaymentType() {
            return (this.copyOfRecipient && this.copyOfRecipient.recipient_info.payment_type)
                || {};
        }

        function filteredPaymentProviders() {
            const vm = this;
            const storedPaymentType = vm.copyOfRecipient.recipient_info.payment_type || {};
            const paymentType = vm.deliveryMethods.find(({ method }) => method === storedPaymentType.method);
            if (!paymentType || !paymentType.providers) return [];
            const providers = paymentType.providers.filter((provider) => {
                if (ServerUtils.isDevOrLocal() && paymentType.method === 'kaoshi-test') {
                    return true;
                }
                return provider.country_abbreviation === vm.countryAbbrev() && provider.name !== 'Flutterwave';
            });

            if (!providers) return [];
            return providers;
        }

        function paymentProviderBranches() {
            if (ServerUtils.isDevOrLocal() && this.selectedPaymentType && this.selectedPaymentType.method === 'kaoshi-test') {
                return [];
            }
            const vm = this;
            if (vm.branches === []) return [];
            const filteredBranches = vm.branches.filter(
                (branch) => branch.provider_id === vm.selectedPaymentProvider.id,
            );
            if (filteredBranches.length === 1) {
                Vue.set(vm, 'selectedBranch', filteredBranches[0]);
            }
            return filteredBranches;
        }

        function pageHeaderClass() {
            return this.$store.state.pageHeaderClass;
        }

        function paymentTypeIsBank() {
            const vm = this;
            if (!vm.copyOfRecipient.recipient_info.payment_type) {
                return false;
            }
            return vm.copyOfRecipient.recipient_info.payment_type.method === 'bank-transfer';
        }

        function paymentTypeIsMobile() {
            const vm = this;
            if (!vm.copyOfRecipient.recipient_info.payment_type) {
                return false;
            }
            return vm.copyOfRecipient.recipient_info.payment_type.method === 'mobile-money';
        }

        function paymentTypeIsPickup() {
            const vm = this;
            if (!vm.copyOfRecipient.recipient_info.payment_type) {
                return false;
            }
            return vm.copyOfRecipient.recipient_info.payment_type.method === 'pickup';
        }

        function phoneRules() {
            const vm = this;
            const recipientCountry = this.countries.find((c) => c.abbreviation === vm.copyOfRecipient.country);
            const recipientCountryName = (recipientCountry && recipientCountry.country) || null;

            if (vm.paymentTypeIsMobile || vm.paymentTypeIsPickup) {
                return `required|country-code:${recipientCountryName}|international-phone:${recipientCountryName}`;
            }
            return `country-code:${recipientCountryName}|international-phone:${recipientCountryName}`;
        }

        function paymentProviderRules() {
            const vm = this;

            const isKaoshiTest = ServerUtils.isDevOrLocal() && this.selectedPaymentType && this.selectedPaymentType.method === 'kaoshi-test';

            if (isKaoshiTest) {
                return {
                    name: 'required|name|full-name|max:255',
                    email_address: 'email',
                    phone_number: '',
                    account_number: '',
                };
            }

            if (!vm.selectedPaymentProvider || vm.paymentProviders.length === 0) {
                return {
                    name: '',
                    email_address: '',
                    phone_number: '',
                    account_number: '',
                };
            }

            const selectedProvider = vm.paymentProviders.find(
                (provider) => provider.provider_id === vm.selectedPaymentProvider.id,
            );

            if (selectedProvider) {
                return selectedProvider.rules.rules;
            }

            return {
                name: 'required|name|full-name|max:255',
                account_number: 'required|string|max:255',
                phone_number: '',
                email_address: 'email',
            };
        }

        function accountNumberRules() {
            if (this.isAllowToValidateAccount) {
                return `${this.paymentProviderRules.account_number}|numeric`;
            } else {
                return `${this.paymentProviderRules.account_number}|numeric|confirmed:${this.confirmAccountNumber}`;
            }
        }

        function isFlutterwaveAvailable() {
            const countryAbbrev = this.countryAbbrev();
            const deliveryMethod = this.selectedPaymentType && this.selectedPaymentType.method;
            const isDeliveryMethodAllowed = deliveryMethod === 'bank-transfer';
            return isDeliveryMethodAllowed && FlutterwaveService.isBanksAvailable(countryAbbrev);
        }

        /**
         * Determine if Flutterwave used
         * Should be used only for some country
         * and only for p2p offers
         */
        function isFlutterwaveUsed() {
            if (ServerUtils.isDevOrLocal() && this.selectedPaymentType && this.selectedPaymentType.method === 'kaoshi-test') {
                return false;
            }
            return this.isAllowToValidateAccount && this.isFlutterwaveAvailable;
        }

        function isAllowToValidateAccount() {
            const countryAbbrev = this.countryAbbrev();
            return !!this.countriesToValidateAccount.find((country) => country === countryAbbrev);
        }

        function isAbleToValidateAccount() {
            return !!this.isAllowToValidateAccount && !!this.selectedBank && !!this.copyOfRecipient.recipient_info.account_number;
        }

        function isBankBranchesAvailable() {
            const countryAbbrev = this.countryAbbrev()
            const isCountryAllowed = this.countriesToAllowBankBranches.find((country) => country === countryAbbrev);
            return !!isCountryAllowed && this.isFlutterwaveAvailable;
        }

        function filteredBanks() {
            const deliveryMethod = this.selectedPaymentType && this.selectedPaymentType.method;
            if (deliveryMethod === 'mobile-money') {
                return this.banks.filter((item) => item.IsMobileVerified);
            } else {
                return this.banks.filter((item) => !item.IsMobileVerified);
            }
        }

        /**
         * Control recipient name row visibility
         * If we validate the account details - show name row if name is resolved by Flutterwave
         * Otherwise - always hide the field
         */
        function showRecipientNameRow() {
            if (this.isAllowToValidateAccount && this.isFlutterwaveAvailable) {
                return !!this.copyOfRecipient.recipient_info.name;
            }
            return false;
        }

        /**
         * Control recipient field visibility
         * If we validate the account details - don't show the field
         * Otherwise - always show the field
         */
        function showRecipientNameField() {
            if (this.isAllowToValidateAccount && this.isFlutterwaveAvailable) {
                return false;
            }
            return true;
        }
    }

    function beforeDestroy() {
        const vm = this;
        vm.$store.commit('hideLogoPart', false);
        vm.$store.commit('removeLogoPartClass', 'header-bar-recipient-details');
    }
</script>

<style lang="scss">

    .header-bar-recipient-details .container a {
        display: none;
    }

    .body-cont1.recipient-edit .page-titel {
        position: relative;
    }

</style>

<style scoped>
.form {
    max-width: 500px;
}

.v-expansion-panels--tile, .v-expansion-panels--tile>.v-expansion-panel:before {
    border-radius: 0;
}

.v-expansion-panels--flat>.v-expansion-panel:before {
    box-shadow: 0 0 0 0 rgba(0,0,0,.2), 0 0 0 0 rgba(0,0,0,.14), 0 0 0 0 rgba(0,0,0,.12);
}

.v-expansion-panel-content__wrap {
    padding: 0 24px 26px;
}

.v-expansion-panel {
    border: 1px solid;
    border-color: rgba(0, 0, 0, 0.38);
    border-radius: 4px !important;
    transition-duration: .3s;
    transition-property: color,border-width;
    transition-timing-function: cubic-bezier(.25,.8,.25,1);
}

.v-expansion-panel-header {
    padding: 12px 12px;
    color: rgba(0,0,0,.6);
    min-height: 56px;
    font-size: 1rem;
}

.v-expansion-panel--active>.v-expansion-panel-header {
    color: rgba(63, 80, 181, 1);
}

/* .v-expansion-panel--active {
    border: 2px solid;
    border-color: rgba(63, 80, 181, 1);
} */

.v-expansion-panel--active>.v-expansion-panel-header--active .v-expansion-panel-header__icon:not(.v-expansion-panel-header__icon--disable-rotate) .v-icon {
    color: rgba(63, 80, 181, 1);
}

.theme--light.v-list-item .v-list-item__mask {
    color: unset;
    background: #ffeec7;
    padding: 0 2px;
}

.theme--dark.v-card.v-card--outlined {
    border: 1px solid #FFD54F;
    background-color: transparent;
}
</style>
